import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Settings as SettingsIcon, KeyRound, User, Building2, Save, ArrowLeft, LogOut, Trash2, ShieldCheck, ShieldOff, Mail, Gavel, Receipt, Users as UsersIcon, Plus, AlertTriangle, Send, Palette, Copy, Upload, Lock, Clock } from "lucide-react";
import EmailTemplatesTab from "./EmailTemplatesTab";
import PlatformEmailTemplatesTab from "./PlatformEmailTemplatesTab";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [twoFA, setTwoFA] = useState({ qr: "", secret: "", uri: "", code: "" });
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [emailChange, setEmailChange] = useState({ new_email: "", password: "", token: "" });
  const [del, setDel] = useState({ password: "", email_confirm: "", submitting: false, error: "" });
  const [transferEmail, setTransferEmail] = useState("");
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [modHistory, setModHistory] = useState(null);
  const [billHistory, setBillHistory] = useState(null);
  const [team, setTeam] = useState(null);
  const [teamAddEmail, setTeamAddEmail] = useState("");
  const [teamError, setTeamError] = useState("");
  // Platform branding (Owner only)
  const [platform, setPlatform] = useState({ site_name: "", tagline: "", logo_url: "", primary_color: "", accent_color: "", footer_disclaimer: "" });
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformLogoUploading, setPlatformLogoUploading] = useState(false);
  const [platformError, setPlatformError] = useState("");
  const [platformSuccess, setPlatformSuccess] = useState("");
  // Join-company (candidates / solo recruiters)
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  // Appeal composer (inline in Moderation tab)
  const [appealComposer, setAppealComposer] = useState(null); // {target_type, target_id, title}
  const [appealMessage, setAppealMessage] = useState("");
  const [appealError, setAppealError] = useState("");
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  // Branding (Growth+ / Enterprise)
  const [branding, setBranding] = useState({ careers_slug: "", careers_display_name: "", brand_logo_url: "", brand_primary_color: "", whitelabel_enabled: false });
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingError, setBrandingError] = useState("");
  const [brandingSuccess, setBrandingSuccess] = useState("");

  // Custom Tracking State for User Settings
  const [userTrackers, setUserTrackers] = useState([]);
  const [trackerCodeLookup, setTrackerCodeLookup] = useState("");
  const [searchedTracker, setSelectedTracker] = useState(null);
  const [searchingTracker, setSearchingTracker] = useState(false);

  // HR Custom Tracking State
  const [showCreateTracker, setShowCreateTracker] = useState(false);
  const [newTracker, setNewTracker] = useState({ title: "", target_id: "", target_type: "candidate", status: "Pending", tracking_code: "", status_color: "#5C5C5C" });
  const [updatingTracker, setUpdatingTracker] = useState(null);
  const [newUpdate, setNewUpdate] = useState({ status: "Reviewing", message: "", status_color: "#3B82F6" });
  const [submittingTracker, setSubmittingTracker] = useState(false);
  const [submittingUpdate, setSubmittingUpdate] = useState(false);

  const handleCreateTracker = async (e) => {
    e?.preventDefault?.();
    if (!newTracker.title.trim() || !newTracker.target_id.trim()) {
      alert("Title and Target ID are required.");
      return;
    }
    setSubmittingTracker(true);
    try {
      await api.post("/recruiter/trackers", {
        title: newTracker.title.trim(),
        target_id: newTracker.target_id.trim(),
        target_type: newTracker.target_type,
        status: newTracker.status,
        status_color: newTracker.status_color,
        tracking_code: newTracker.tracking_code.trim() || undefined
      });
      alert("Tracker successfully created!");
      setShowCreateTracker(false);
      setNewTracker({ title: "", target_id: "", target_type: "candidate", status: "Pending", tracking_code: "", status_color: "#5C5C5C" });
      loadUserTrackers();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setSubmittingTracker(false);
    }
  };

  const handleAddUpdate = async (e) => {
    e?.preventDefault?.();
    if (!newUpdate.message.trim()) {
      alert("Update message is required.");
      return;
    }
    setSubmittingUpdate(true);
    try {
      await api.post(`/recruiter/trackers/${updatingTracker.tracker_id}/updates`, {
        status: newUpdate.status,
        message: newUpdate.message.trim(),
        status_color: newUpdate.status_color
      });
      alert("Update posted successfully!");
      setUpdatingTracker(null);
      setNewUpdate({ status: "Reviewing", message: "", status_color: "#3B82F6" });
      loadUserTrackers();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setSubmittingUpdate(false);
    }
  };

  const handleDeleteTracker = async (tid) => {
    if (!window.confirm("Are you sure you want to delete this tracker permanently?")) return;
    try {
      await api.delete(`/recruiter/trackers/${tid}`);
      alert("Tracker deleted.");
      loadUserTrackers();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };
  
  const loadUserTrackers = async () => {
    try {
      const endpoint = (user?.role === "recruiter" || user?.role === "owner") ? "/recruiter/trackers" : "/candidate/trackers";
      const { data } = await api.get(endpoint);
      setUserTrackers(data || []);
    } catch {
      setUserTrackers([]);
    }
  };

  const lookupTracker = async (e) => {
    e?.preventDefault?.();
    if (!trackerCodeLookup.trim()) return;
    setSearchingTracker(true);
    setSelectedTracker(null);
    try {
      const { data } = await api.get(`/public/trackers/${trackerCodeLookup.trim().toUpperCase()}`);
      setSelectedTracker(data);
    } catch (e) {
      alert("No tracker found with that tracking code.");
    } finally {
      setSearchingTracker(false);
    }
  };

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Custom Pipeline States
  const [customStages, setCustomStages] = useState([]);
  const [savingCustomStages, setSavingCustomStages] = useState(false);

  // Recruiter Employee Invites States
  const [companyInvites, setCompanyInvites] = useState([]);
  const [generatingInvite, setGeneratingInvite] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        linkedin_url: user.linkedin_url || "",
        company_name: user.company_name || "",
        company_website: user.company_website || "",
        job_title: user.job_title || "",
        org_type: user.org_type || "business",
      });
    }
  }, [user]);

  // Auto-verify email change when the user lands here from the verification email link.
  useEffect(() => {
    const t = searchParams.get("email_verify_token");
    if (!t) return;
    setTab("email");
    (async () => {
      try {
        const { data } = await api.post("/users/me/email/verify", { token: t });
        await refresh();
        setSuccess(`Email updated to ${data.email}.`);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setSearchParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (!user || user === false) return <div className="p-16">Loading…</div>;

  const isCandidate = user.role === "candidate";
  const isRecruiter = user.role === "recruiter";

  const loadModHistory = async () => {
    try { const { data } = await api.get("/users/me/moderation-history"); setModHistory(data); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const loadBillHistory = async () => {
    try { const { data } = await api.get("/users/me/billing-history"); setBillHistory(data); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const loadTeam = async () => {
    setTeamError("");
    try {
      const { data } = await api.get("/recruiter/my-company");
      setTeam(data);
      if (data?.company) {
        setBranding({
          careers_slug: data.company.careers_slug || "",
          careers_display_name: data.company.careers_display_name || "",
          brand_logo_url: data.company.brand_logo_url || "",
          brand_primary_color: data.company.brand_primary_color || "",
          whitelabel_enabled: !!data.company.whitelabel_enabled,
        });
      }
    } catch (e) { setTeamError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveBranding = async (e) => {
    e?.preventDefault?.();
    if (!team?.company?.company_id) return;
    setBrandingSaving(true); setBrandingError(""); setBrandingSuccess("");
    try {
      await api.put(`/companies/${team.company.company_id}/branding`, branding);
      setBrandingSuccess("Branding saved.");
      loadTeam();
    } catch (e) {
      setBrandingError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBrandingSaving(false); }
  };
  const addTeamMember = async (e) => {
    e?.preventDefault?.();
    if (!team?.company?.company_id) return;
    setTeamError("");
    try {
      await api.post(`/companies/${team.company.company_id}/members`, { email: teamAddEmail.toLowerCase().trim() });
      setTeamAddEmail("");
      loadTeam();
    } catch (e) {
      setTeamError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const removeTeamMember = async (uid) => {
    if (!team?.company?.company_id) return;
    if (!window.confirm("Remove this team member from your company?")) return;
    try {
      await api.delete(`/companies/${team.company.company_id}/members/${uid}`);
      loadTeam();
    } catch (e) {
      setTeamError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitAppeal = async (e) => {
    e?.preventDefault?.();
    if (!appealComposer) return;
    setAppealError(""); setAppealSubmitting(true);
    try {
      await api.post("/appeals", {
        message: appealMessage,
        target_type: appealComposer.target_type,
        target_id: appealComposer.target_id || undefined,
      });
      setAppealComposer(null); setAppealMessage("");
      await loadModHistory();
      await refresh();
      setSuccess("Appeal submitted. A moderator will review it within five business days.");
    } catch (e) {
      setAppealError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setAppealSubmitting(false); }
  };

  // Index open appeals by `${target_type}:${target_id}` so each row knows if it's already pending.
  const openAppealsByKey = (() => {
    const map = {};
    (modHistory?.appeals || []).forEach((a) => {
      if (a.status === "open") {
        const k = `${a.target_type || "ban"}:${a.target_id || ""}`;
        map[k] = a;
      }
    });
    return map;
  })();
  // Resolved appeals by key — surface latest resolution result inline.
  const resolvedAppealByKey = (() => {
    const map = {};
    (modHistory?.appeals || []).forEach((a) => {
      if (a.status === "resolved") {
        const k = `${a.target_type || "ban"}:${a.target_id || ""}`;
        if (!map[k]) map[k] = a;  // first one (already sorted newest-first by API)
      }
    });
    return map;
  })();

  const loadCustomPipelineInSettings = async () => {
    const compId = user?.company_id;
    if (compId) {
      try {
        const { data } = await api.get(`/companies/${compId}/pipeline-stages`);
        setCustomStages(data.stages || []);
      } catch (e) {
        setCustomStages([]);
      }
    }
  };

  const loadCompanyInvites = async () => {
    try {
      const { data } = await api.get("/recruiter/company/invite");
      setCompanyInvites(data.invites || []);
    } catch {
      setCompanyInvites([]);
    }
  };

  const switchTab = (v) => {
    setTab(v); setError(""); setSuccess("");
    if (v === "moderation" && !modHistory) loadModHistory();
    if (v === "billing" && !billHistory) loadBillHistory();
    if (v === "team" && !team) { loadTeam(); loadCompanyInvites(); }
    if (v === "branding" && !team) loadTeam();
    if (v === "platform" && !platform.site_name) loadPlatform();
    if (v === "business") loadCustomPipelineInSettings();
    if (v === "trackers") loadUserTrackers();
  };

  const loadPlatform = async () => {
    try { const { data } = await api.get("/platform/branding"); setPlatform(data); }
    catch (e) { setPlatformError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const savePlatform = async (e) => {
    e?.preventDefault?.();
    setPlatformSaving(true); setPlatformError(""); setPlatformSuccess("");
    try {
      const { data } = await api.put("/admin/platform/branding", platform);
      setPlatform(data);
      setPlatformSuccess("Platform branding saved. Reloading…");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      setPlatformError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPlatformSaving(false); }
  };
  const uploadPlatformLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPlatformLogoUploading(true); setPlatformError(""); setPlatformSuccess("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/platform/logo-upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPlatform((p) => ({ ...p, logo_url: data.logo_url }));
      setPlatformSuccess("Logo uploaded. Don't forget to Save platform brand.");
    } catch (err) {
      setPlatformError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setPlatformLogoUploading(false); e.target.value = ""; }
  };

  const doJoinCompany = async (e) => {
    e?.preventDefault?.();
    setJoinError(""); setJoinSubmitting(true);
    try {
      const { data } = await api.post("/users/me/join-company", { code: joinCode });
      await refresh();
      setSuccess(`Joined ${data.company_name}.`);
      setJoinCode("");
    } catch (e) {
      setJoinError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setJoinSubmitting(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const payload = { name: form.name, phone: form.phone, linkedin_url: form.linkedin_url };
      if (!isCandidate) {
        Object.assign(payload, {
          company_name: form.company_name, company_website: form.company_website, job_title: form.job_title, org_type: form.org_type,
        });
      }
      await api.put("/users/me", payload);
      // PUT /users/me drops org_type for non-recruiters. If the Owner toggles it
      // from their own Business tab, mirror the change onto their company doc so
      // the careers page disclaimer reacts immediately.
      if (!isCandidate && user.role !== "recruiter" && user.company_id && form.org_type) {
        try { await api.put(`/companies/${user.company_id}/branding`, { org_type: form.org_type }); } catch { /* non-recruiter Owner mirror — best-effort, profile save already succeeded */ }
      }
      await refresh();
      setSuccess("Saved.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (pwd.new_password !== pwd.confirm) { setError("New passwords don't match"); return; }
    if (pwd.new_password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.put("/users/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setSuccess("Password updated.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const start2FA = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
      const { data } = await api.post("/users/me/2fa/setup");
      setTwoFA({ qr: data.qr_data_url, secret: data.secret, uri: data.uri, code: "" });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      await api.post("/users/me/2fa/verify", { code: twoFA.code.replace(/\s+/g, "") });
      setTwoFA({ qr: "", secret: "", uri: "", code: "" });
      await refresh();
      setSuccess("Two-factor authentication enabled.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      await api.post("/users/me/2fa/disable", {
        password: disablePwd,
        code: disableCode.replace(/\s+/g, "") || undefined,
      });
      setDisablePwd(""); setDisableCode("");
      await refresh();
      setSuccess("Two-factor authentication disabled.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const { data } = await api.post("/users/me/email/request-change", {
        new_email: emailChange.new_email.toLowerCase().trim(),
        password: emailChange.password,
      });
      setEmailChange({ new_email: emailChange.new_email, password: "", token: "" });
      await refresh();
      setSuccess(data.message || `Verification email sent to ${emailChange.new_email}. Check your inbox to confirm.`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verifyEmailChange = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const { data } = await api.post("/users/me/email/verify", { token: emailChange.token.trim() });
      setEmailChange({ new_email: "", password: "", token: "" });
      await refresh();
      setSuccess(`Email updated to ${data.email}.`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const cancelEmailChange = async () => {
    setError(""); setSuccess("");
    try {
      await api.post("/users/me/email/cancel-change");
      setEmailChange({ new_email: "", password: "", token: "" });
      await refresh();
      setSuccess("Email change cancelled.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setDel((d) => ({ ...d, submitting: true, error: "" }));
    try {
      await api.delete("/auth/me", { data: { password: del.password || null, email_confirm: del.email_confirm } });
      // Account gone — bounce to landing
      window.location.href = "/";
    } catch (err) {
      setDel((d) => ({ ...d, submitting: false, error: formatApiError(err.response?.data?.detail) || err.message }));
    }
  };

  const transferCompany = async (e) => {
    e?.preventDefault?.();
    if (!team?.company?.company_id) return;
    if (!window.confirm(`Permanently transfer ownership of ${team.company.name} to ${transferEmail}? You will become a regular team member.`)) return;
    setTransferSubmitting(true); setTeamError("");
    try {
      await api.post(`/companies/${team.company.company_id}/transfer`, { new_owner_email: transferEmail.toLowerCase().trim() });
      setTransferEmail("");
      await refresh();
      await loadTeam();
      setSuccess("Ownership transferred. You're now a team member.");
    } catch (e) {
      setTeamError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setTransferSubmitting(false); }
  };

  const inputCls = "w-full border border-[#0F0F0F] px-3 py-2 bg-transparent rounded-sm";
  const back = user.role === "owner" ? "/admin" : user.role === "recruiter" ? "/recruiter" : "/candidate";

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <Link to={back} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="mt-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><SettingsIcon className="w-3 h-3" /> Settings</div>
          <h1 className="font-display font-black text-5xl mt-3 tracking-tight">Your account.</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">
            Signed in as <strong>{user.email}</strong> · {user.role}
            {user.account_number && (
              <span className="ml-2 bg-[#F3F2EB] px-2 py-0.5 font-mono text-xs border border-[#E2E0D8]" data-testid="settings-account-number">
                Account No: <strong>{user.account_number}</strong>
              </span>
            )}
            {user.custom_tags && user.custom_tags.map((tag) => (
              <span key={tag} className="ml-2 bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider" data-testid={`settings-user-tag-${tag}`}>
                {tag}
              </span>
            ))}
          </p>
        </div>
        <button onClick={async () => { await logout(); window.location.href = "/"; }} className="btn-ghost text-sm">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="mt-8 border-b border-[#E2E0D8] flex gap-1 overflow-x-auto">
        {[
          { v: "profile", t: "Profile", icon: User },
          { v: "connections", t: "Connected Accounts", icon: KeyRound },
          { v: "business", t: "Business", icon: Building2, hide: isCandidate },
          { v: "team", t: "Team", icon: UsersIcon, hide: !isRecruiter && user.role !== "owner" },
          { v: "branding", t: "Branding", icon: Palette, hide: !isRecruiter && user.role !== "owner" },
          { v: "platform", t: "Platform brand", icon: Palette, hide: user.role !== "owner" },
          { v: "join", t: "Join company", icon: UsersIcon },
          { v: "email", t: "Email", icon: Mail },
          { v: "templates", t: "Email templates", icon: Mail, hide: !isRecruiter && user.role !== "owner" },
          { v: "platform-templates", t: "Platform emails", icon: Mail, hide: user.role !== "owner" },
          { v: "security", t: "Security", icon: KeyRound },
          { v: "twofa", t: "Two-factor", icon: ShieldCheck },
          { v: "moderation", t: "Moderation", icon: Gavel },
          { v: "billing", t: "Billing history", icon: Receipt, hide: !isRecruiter && user.role !== "owner" },
          { v: "trackers", t: "Trackers", icon: Clock },
        ].filter((t) => !t.hide).map((t) => {
          const Icon = t.icon;
          const active = tab === t.v;
          return (
            <button key={t.v} onClick={() => switchTab(t.v)} data-testid={`settings-tab-${t.v}`}
              className={`px-5 py-3 font-display font-bold text-sm border-b-2 -mb-px flex items-center gap-2 ${active ? "border-[var(--brand-primary)] text-[#0F0F0F]" : "border-transparent text-[#5C5C5C] hover:text-[#0F0F0F]"}`}>
              <Icon className="w-4 h-4" /> {t.t}
            </button>
          );
        })}
      </div>

      {error && <div data-testid="settings-error" className="mt-4 text-sm text-[var(--brand-primary)]">{error}</div>}
      {success && <div data-testid="settings-success" className="mt-4 text-sm text-green-700">{success}</div>}

      {tab === "platform" && (
        <div className="mt-6 max-w-2xl" data-testid="platform-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Palette className="w-3 h-3 text-[var(--brand-primary)]" /> Platform brand</div>
          <h2 className="font-display font-black text-2xl mt-2">Make Ocean Ash yours.</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">These settings change the platform's own name, tagline, logo, and primary accent — visible across the entire site to every visitor.</p>
          <form onSubmit={savePlatform} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Site name</label>
                <input data-testid="platform-site-name" value={platform.site_name} onChange={(e) => setPlatform({ ...platform, site_name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Tagline</label>
                <input data-testid="platform-tagline" value={platform.tagline} onChange={(e) => setPlatform({ ...platform, tagline: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-eyebrow block mb-1.5">Logo</label>
                <div className="flex items-start gap-3 flex-wrap">
                  {platform.logo_url ? (
                    <img src={platform.logo_url} alt="Platform logo preview" data-testid="platform-logo-preview" className="w-16 h-16 object-contain border border-[#0F0F0F] bg-white p-1" />
                  ) : (
                    <div className="w-16 h-16 border border-dashed border-[#0F0F0F]/40 grid place-items-center text-[10px] text-[#5C5C5C]">no logo</div>
                  )}
                  <div className="flex-1 min-w-[220px] space-y-2">
                    <input data-testid="platform-logo" value={platform.logo_url} onChange={(e) => setPlatform({ ...platform, logo_url: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono text-xs" placeholder="https://oceanash.org/logo.svg" />
                    <label data-testid="platform-logo-upload-label" className="inline-flex items-center gap-2 cursor-pointer btn-ghost text-xs">
                      <Upload className="w-3 h-3" />
                      {platformLogoUploading ? "Uploading…" : "Upload an image (PNG/JPG/SVG, max 4MB)"}
                      <input data-testid="platform-logo-upload" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={uploadPlatformLogo} className="hidden" />
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Primary colour (hex)</label>
                <div className="flex gap-2">
                  <input data-testid="platform-primary" value={platform.primary_color} onChange={(e) => setPlatform({ ...platform, primary_color: e.target.value })} className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono" placeholder="#D94833" />
                  <span className="inline-block w-10 h-10 border border-[#0F0F0F]" style={{ background: platform.primary_color || "transparent" }} />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Accent colour (hex)</label>
                <div className="flex gap-2">
                  <input data-testid="platform-accent" value={platform.accent_color} onChange={(e) => setPlatform({ ...platform, accent_color: e.target.value })} className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono" placeholder="#0F0F0F" />
                  <span className="inline-block w-10 h-10 border border-[#0F0F0F]" style={{ background: platform.accent_color || "transparent" }} />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="label-eyebrow block mb-1.5">Custom footer disclaimer (optional — overrides default)</label>
                <textarea data-testid="platform-disclaimer" rows={4} value={platform.footer_disclaimer} onChange={(e) => setPlatform({ ...platform, footer_disclaimer: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
              </div>
            </div>
            {platformError && <div data-testid="platform-error" className="text-sm text-[var(--brand-primary)]">{platformError}</div>}
            {platformSuccess && <div data-testid="platform-success" className="text-sm text-green-700">{platformSuccess}</div>}
            <button disabled={platformSaving} data-testid="platform-save" type="submit" className="btn-terra">
              <Save className="w-4 h-4" /> {platformSaving ? "Saving…" : "Save platform brand"}
            </button>
          </form>
        </div>
      )}

      {tab === "join" && (
        <div className="mt-6 max-w-xl" data-testid="join-section">
          <div className="label-eyebrow flex items-center gap-1.5"><UsersIcon className="w-3 h-3" /> Join a company</div>
          <h2 className="font-display font-black text-2xl mt-2">Have an invite code?</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">Paste the recruiter invite code from your team and you'll join their company instantly. Candidates get promoted to recruiter automatically.</p>
          <form onSubmit={doJoinCompany} className="mt-6 space-y-3">
            <label className="label-eyebrow block mb-1.5">Invite code</label>
            <input data-testid="join-code" required value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono tracking-widest" placeholder="ABC1234XYZ" />
            {joinError && <div data-testid="join-error" className="text-sm text-[var(--brand-primary)]">{joinError}</div>}
            <button disabled={joinSubmitting || !joinCode} data-testid="join-submit" type="submit" className="btn-terra">
              <UsersIcon className="w-4 h-4" /> {joinSubmitting ? "Joining…" : "Join company"}
            </button>
          </form>
        </div>
      )}

      {tab === "branding" && (
        <div className="mt-6 max-w-2xl" data-testid="branding-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Palette className="w-3 h-3 text-[var(--brand-primary)]" /> Branding</div>
          <h2 className="font-display font-black text-2xl mt-2">Make it yours.</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">A custom careers page is available on Growth plans. Logo, brand colours, and white-label mode are unlocked on Enterprise.</p>

          {!team ? (
            <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>
          ) : !team.company ? (
            <div className="mt-6 text-sm text-[#5C5C5C] border border-[#E2E0D8] p-5">No company on your account.</div>
          ) : !team.is_company_owner ? (
            <div className="mt-6 text-sm text-[#5C5C5C] border border-[#E2E0D8] p-5">Only the company owner can edit branding. Contact your team's founder to make changes.</div>
          ) : (
            <form onSubmit={saveBranding} className="mt-6 space-y-5">
              {/* Growth fields */}
              <div className="border border-[#E2E0D8] p-5">
                <div className="label-eyebrow text-[#0F0F0F]">Custom careers page · Growth+</div>
                <p className="text-xs text-[#5C5C5C] mt-1">Your roles will live at <code>{window.location.origin}/c/<strong>{branding.careers_slug || "your-slug"}</strong></code></p>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label-eyebrow block mb-1.5">Careers URL slug</label>
                    <input data-testid="branding-slug" value={branding.careers_slug} onChange={(e) => setBranding({ ...branding, careers_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="acme" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">Display name</label>
                    <input data-testid="branding-display-name" value={branding.careers_display_name} onChange={(e) => setBranding({ ...branding, careers_display_name: e.target.value })} placeholder="Acme Careers" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
                  </div>
                </div>
                {branding.careers_slug && (
                  <div className="mt-3">
                    <a target="_blank" rel="noreferrer" href={`/c/${branding.careers_slug}`} data-testid="branding-preview-link" className="inline-flex items-center gap-1.5 text-xs font-semibold hover:text-[var(--brand-primary)]">
                      Preview → /c/{branding.careers_slug}
                    </a>
                  </div>
                )}
              </div>

              {/* Enterprise fields */}
              <div className="border border-[#0F0F0F] p-5 bg-[#0F0F0F]/5">
                <div className="label-eyebrow text-[var(--brand-primary)]">Logo, colour & white-label · Enterprise</div>
                <p className="text-xs text-[#5C5C5C] mt-1">Replace the Ocean Ash branding on your careers page with your own logo + accent colour. Turn on white-label mode to hide every platform mention.</p>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="label-eyebrow block mb-1.5">Logo URL</label>
                    <input data-testid="branding-logo" value={branding.brand_logo_url} onChange={(e) => setBranding({ ...branding, brand_logo_url: e.target.value })} placeholder="https://acme.com/logo.svg" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono text-xs" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">Primary colour (hex)</label>
                    <div className="flex gap-2 items-center">
                      <input data-testid="branding-color" value={branding.brand_primary_color} onChange={(e) => setBranding({ ...branding, brand_primary_color: e.target.value })} placeholder="#D94833" className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono" />
                      <span className="inline-block w-10 h-10 border border-[#0F0F0F]" style={{ background: branding.brand_primary_color || "transparent" }} />
                    </div>
                  </div>
                  <div className="md:col-span-2 border border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/5 p-3 flex items-start gap-2.5">
                    <input data-testid="branding-whitelabel" type="checkbox" checked={!!branding.whitelabel_enabled} onChange={(e) => setBranding({ ...branding, whitelabel_enabled: e.target.checked })} className="mt-0.5" />
                    <div className="text-sm">
                      <div className="font-display font-bold">White-label mode <span className="text-[10px] text-[var(--brand-primary)] uppercase tracking-widest ml-1">Enterprise</span></div>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">When enabled, your <code>/c/{branding.careers_slug || "&lt;slug&gt;"}</code> careers page hides every Ocean Ash brand mention — no &ldquo;Powered by&rdquo; line, no platform logo, no platform nav, no cross-links. Only the company&rsquo;s own logo, colours, and copy remain.</div>
                    </div>
                  </div>
                </div>
              </div>

              {brandingError && <div data-testid="branding-error" className="text-sm text-[var(--brand-primary)]">{brandingError}</div>}
              {brandingSuccess && <div data-testid="branding-success" className="text-sm text-green-700">{brandingSuccess}</div>}
              <button disabled={brandingSaving} data-testid="branding-save" type="submit" className="btn-terra">
                <Save className="w-4 h-4" /> {brandingSaving ? "Saving…" : "Save branding"}
              </button>

              {branding.careers_slug && (
                <div className="mt-6 border-t border-[#E2E0D8] pt-4">
                  <div className="label-eyebrow">Share your careers page</div>
                  <div className="flex gap-2 mt-2 items-center">
                    <input readOnly value={`${window.location.origin}/c/${branding.careers_slug}`} className="flex-1 border border-[#E2E0D8] bg-transparent px-3 py-2 font-mono text-xs text-[#5C5C5C]" />
                    <button type="button" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/c/${branding.careers_slug}`)} className="btn-ghost text-sm"><Copy className="w-3.5 h-3.5" /> Copy</button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {tab === "moderation" && (
        <div className="mt-6 max-w-2xl" data-testid="moderation-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Gavel className="w-3 h-3" /> Moderation history</div>
          <h2 className="font-display font-black text-2xl mt-2">Account standing.</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">Every warning, limit, denial, ban, and appeal filed on this account.</p>
          {!modHistory ? (
            <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="border border-[#E2E0D8] p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Active warnings</div>
                  <div className="font-display font-black text-3xl mt-1">{modHistory.warnings_count || 0}</div>
                </div>
                <div className="border border-[#E2E0D8] p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Banned</div>
                  <div className={`font-display font-black text-3xl mt-1 ${modHistory.is_banned ? "text-[var(--brand-primary)]" : ""}`}>{modHistory.is_banned ? "Yes" : "No"}</div>
                </div>
                <div className="border border-[#E2E0D8] p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Limited</div>
                  <div className={`font-display font-black text-3xl mt-1 ${modHistory.is_limited ? "text-amber-600" : ""}`}>{modHistory.is_limited ? "Yes" : "No"}</div>
                </div>
              </div>

              {modHistory.is_banned && (() => {
                const key = "ban:";
                const pending = openAppealsByKey[key];
                const resolved = !pending && resolvedAppealByKey[key];
                return (
                  <div className="mt-5 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-4" data-testid="mod-ban">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="label-eyebrow text-[var(--brand-primary)]">Account banned</div>
                        <div className="text-sm mt-1">{modHistory.ban_reason || "—"}</div>
                        {modHistory.ban_note && <div className="text-xs text-[#5C5C5C] mt-1">{modHistory.ban_note}</div>}
                        {modHistory.banned_at && <div className="text-xs text-[#5C5C5C] mt-1">{new Date(modHistory.banned_at).toLocaleString()}</div>}
                      </div>
                      <div className="flex-shrink-0">
                        {pending ? (
                          <span data-testid="appeal-pending-ban" className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)] text-white px-2 py-1">Appeal pending</span>
                        ) : (
                          <button onClick={() => { setAppealComposer({ target_type: "ban", target_id: "", title: modHistory.ban_reason || "Account banned" }); setAppealMessage(""); setAppealError(""); }}
                            data-testid="appeal-btn-ban" className="text-xs border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Appeal</button>
                        )}
                      </div>
                    </div>
                    {resolved && (
                      <div className="mt-3 text-xs border-t border-[var(--brand-primary)]/30 pt-2" data-testid="appeal-result-ban">
                        Appeal <strong className="uppercase">{resolved.action}</strong> · {new Date(resolved.resolved_at || resolved.created_at).toLocaleString()}
                        {resolved.resolution_note && <div className="italic mt-1 text-[#5C5C5C]">Moderator note: {resolved.resolution_note}</div>}
                      </div>
                    )}
                  </div>
                );
              })()}

              {modHistory.is_limited && (() => {
                const key = "limit:";
                const pending = openAppealsByKey[key];
                const resolved = !pending && resolvedAppealByKey[key];
                return (
                  <div className="mt-3 border border-amber-400 bg-amber-50 p-4" data-testid="mod-limit">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="label-eyebrow text-amber-700">Account limited</div>
                        <div className="text-sm mt-1">{modHistory.limited_reason || "—"}</div>
                        {modHistory.limited_note && <div className="text-xs text-[#5C5C5C] mt-1">{modHistory.limited_note}</div>}
                      </div>
                      <div className="flex-shrink-0">
                        {pending ? (
                          <span data-testid="appeal-pending-limit" className="text-[10px] uppercase tracking-widest font-semibold bg-amber-600 text-white px-2 py-1">Appeal pending</span>
                        ) : (
                          <button onClick={() => { setAppealComposer({ target_type: "limit", target_id: "", title: modHistory.limited_reason || "Account limited" }); setAppealMessage(""); setAppealError(""); }}
                            data-testid="appeal-btn-limit" className="text-xs border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Appeal</button>
                        )}
                      </div>
                    </div>
                    {resolved && (
                      <div className="mt-3 text-xs border-t border-amber-300 pt-2" data-testid="appeal-result-limit">
                        Appeal <strong className="uppercase">{resolved.action}</strong> · {new Date(resolved.resolved_at || resolved.created_at).toLocaleString()}
                        {resolved.resolution_note && <div className="italic mt-1 text-[#5C5C5C]">Moderator note: {resolved.resolution_note}</div>}
                      </div>
                    )}
                  </div>
                );
              })()}

              {modHistory.is_denied && (() => {
                const key = "denial:";
                const pending = openAppealsByKey[key];
                const resolved = !pending && resolvedAppealByKey[key];
                return (
                  <div className="mt-3 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-4" data-testid="mod-denial">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="label-eyebrow text-[var(--brand-primary)]">Recruiter denied</div>
                        <div className="text-sm mt-1">{modHistory.denial_reason || "—"}</div>
                      </div>
                      <div className="flex-shrink-0">
                        {pending ? (
                          <span data-testid="appeal-pending-denial" className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)] text-white px-2 py-1">Appeal pending</span>
                        ) : (
                          <button onClick={() => { setAppealComposer({ target_type: "denial", target_id: "", title: modHistory.denial_reason || "Recruiter denied" }); setAppealMessage(""); setAppealError(""); }}
                            data-testid="appeal-btn-denial" className="text-xs border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Appeal</button>
                        )}
                      </div>
                    </div>
                    {resolved && (
                      <div className="mt-3 text-xs border-t border-[var(--brand-primary)]/30 pt-2" data-testid="appeal-result-denial">
                        Appeal <strong className="uppercase">{resolved.action}</strong> · {new Date(resolved.resolved_at || resolved.created_at).toLocaleString()}
                        {resolved.resolution_note && <div className="italic mt-1 text-[#5C5C5C]">Moderator note: {resolved.resolution_note}</div>}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-6">
                <div className="label-eyebrow flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-amber-600" /> Warnings ({(modHistory.warnings || []).length})</div>
                {(modHistory.warnings || []).length === 0 ? (
                  <div className="mt-3 text-sm text-[#5C5C5C]">No warnings on your account.</div>
                ) : (
                  <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
                    {modHistory.warnings.slice().reverse().map((w) => {
                      const key = `warning:${w.id}`;
                      const pending = openAppealsByKey[key];
                      const resolved = !pending && resolvedAppealByKey[key];
                      return (
                        <div key={w.id} data-testid={`mod-warning-${w.id}`} className={`bg-[#FDFCF8] p-3 ${w.voided ? "opacity-60" : ""}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${w.voided ? "bg-[#E2E0D8]" : "bg-amber-100 text-amber-900"}`}>{w.voided ? "voided" : "active"}</span>
                                <span className="text-xs text-[#5C5C5C]">{w.at ? new Date(w.at).toLocaleString() : ""}</span>
                              </div>
                              <div className="font-display font-bold mt-1">{w.reason}</div>
                              {w.note && <div className="text-sm text-[#5C5C5C] mt-1">{w.note}</div>}
                            </div>
                            {!w.voided && (
                              <div className="flex-shrink-0">
                                {pending ? (
                                  <span data-testid={`appeal-pending-warning-${w.id}`} className="text-[10px] uppercase tracking-widest font-semibold bg-amber-600 text-white px-2 py-1">Appeal pending</span>
                                ) : (
                                  <button onClick={() => { setAppealComposer({ target_type: "warning", target_id: w.id, title: w.reason || "Warning" }); setAppealMessage(""); setAppealError(""); }}
                                    data-testid={`appeal-btn-warning-${w.id}`} className="text-xs border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8]">Appeal</button>
                                )}
                              </div>
                            )}
                          </div>
                          {resolved && (
                            <div className="mt-2 text-xs border-t border-[#E2E0D8] pt-2" data-testid={`appeal-result-warning-${w.id}`}>
                              Appeal <strong className="uppercase">{resolved.action}</strong> · {new Date(resolved.resolved_at || resolved.created_at).toLocaleString()}
                              {resolved.resolution_note && <div className="italic mt-1 text-[#5C5C5C]">Moderator note: {resolved.resolution_note}</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-[#5C5C5C] mt-3">
                  To appeal a warning, ban, limit, or denial, visit the <Link className="underline hover:text-[var(--brand-primary)]" to="/appeal">Appeals page</Link>.
                </p>
              </div>

              <div className="mt-6">
                <div className="label-eyebrow">Appeals ({(modHistory.appeals || []).length})</div>
                {(modHistory.appeals || []).length === 0 ? (
                  <div className="mt-3 text-sm text-[#5C5C5C]">You haven't filed any appeals.</div>
                ) : (
                  <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
                    {modHistory.appeals.map((a) => (
                      <div key={a.appeal_id} className="bg-[#FDFCF8] p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${a.status === "open" ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{a.status}{a.action ? ` · ${a.action}` : ""}</span>
                          <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{a.target_type || "ban"}</span>
                          <span className="text-xs text-[#5C5C5C]">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{a.message}</p>
                        {a.resolution_note && <div className="text-xs text-[#5C5C5C] mt-1 italic">Moderator note: {a.resolution_note}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Inline appeal composer (Moderation tab) */}
      {appealComposer && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setAppealComposer(null)} data-testid="settings-appeal-modal">
          <form onSubmit={submitAppeal} className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Send className="w-3 h-3" /> Appeal · {appealComposer.target_type}</div>
            <h3 className="font-display font-black text-2xl mt-1">Explain your appeal.</h3>
            <p className="text-sm text-[#5C5C5C] mt-2">Subject: <em>{appealComposer.title}</em></p>
            <label className="label-eyebrow block mb-1.5 mt-5">Your appeal (min 20 chars)</label>
            <textarea
              data-testid="settings-appeal-message"
              required minLength={20} rows={6}
              value={appealMessage} onChange={(e) => setAppealMessage(e.target.value)}
              className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
              placeholder="Provide context, evidence, what's changed — anything that helps a moderator re-evaluate."
            />
            {appealError && <div data-testid="settings-appeal-error" className="text-sm text-[var(--brand-primary)] mt-2">{appealError}</div>}
            <div className="flex gap-2 mt-4">
              <button disabled={appealSubmitting || appealMessage.length < 20} data-testid="settings-appeal-submit" type="submit" className="btn-terra flex-1 disabled:opacity-50">
                <Send className="w-4 h-4" /> {appealSubmitting ? "Submitting…" : "Submit appeal"}
              </button>
              <button type="button" onClick={() => setAppealComposer(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {tab === "billing" && (
        <div className="mt-6 max-w-2xl" data-testid="billing-history-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Receipt className="w-3 h-3" /> Billing history</div>
          <h2 className="font-display font-black text-2xl mt-2">Receipts & subscriptions.</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">Every Stripe checkout, subscription change, and per-job boost ever billed on this account.</p>
          {!billHistory ? (
            <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>
          ) : (
            <>
              {billHistory.subscription_inherited_from_company && (
                <div className="mt-6 border border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8] p-4" data-testid="plan-inherited-banner">
                  <div className="label-eyebrow text-[var(--brand-primary)]">Inherited plan</div>
                  <div className="font-display font-black text-xl mt-1">You're on your company's plan.</div>
                  <p className="text-sm text-[#FDFCF8]/80 mt-1">
                    Your seat is paid for by the company founder. Plan upgrades, downgrades, and billing receipts are managed by them.
                    You can still see boosts and one-off charges you triggered below.
                  </p>
                </div>
              )}
              <div className="mt-6 border border-[#0F0F0F] p-4">
                <div className="label-eyebrow">Current plan</div>
                <div className="font-display font-black text-2xl mt-1 capitalize">{billHistory.current_tier}</div>
                <div className="text-xs text-[#5C5C5C] mt-0.5">Billed {billHistory.current_cycle}</div>
                <div className="mt-3">
                  <Link to="/billing" className="btn-ghost text-sm">Manage plan</Link>
                </div>
              </div>

              <div className="mt-6">
                <div className="label-eyebrow">Transactions ({(billHistory.transactions || []).length})</div>
                {(billHistory.transactions || []).length === 0 ? (
                  <div className="mt-3 text-sm text-[#5C5C5C]">No transactions yet.</div>
                ) : (
                  <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
                    {billHistory.transactions.map((t) => (
                      <div key={t.transaction_id} data-testid={`bill-txn-${t.transaction_id}`} className="bg-[#FDFCF8] p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {(() => {
                              const ds = t.display_status || (t.status === "completed" ? "APPROVED" : "PENDING");
                              const cls = ds === "APPROVED" ? "bg-green-100 text-green-800"
                                : ds === "DECLINED" ? "bg-[var(--brand-primary)] text-white"
                                : ds === "CANCELLED" ? "bg-[#0F0F0F] text-white"
                                : "bg-[#E2E0D8]";
                              return <span data-testid={`bill-status-${t.transaction_id}`} className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${cls}`}>{ds}</span>;
                            })()}
                            <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{t.kind || "subscription"}</span>
                            {t.discount?.code && (
                              <span className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/40 px-1.5 py-0.5">
                                -{t.discount.percent_off}% · {t.discount.code}
                              </span>
                            )}
                          </div>
                          <div className="font-display font-bold mt-1 capitalize">
                            {t.kind === "boost" ? `Boost · ${t.tier} — ${t.job_title || t.job_id}` : `${t.tier} (${t.cycle})`}
                          </div>
                          <div className="text-xs text-[#5C5C5C] mt-0.5">{new Date(t.created_at).toLocaleString()}</div>
                        </div>
                        <div className="font-display font-bold">£{t.amount_gbp ?? 0}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(billHistory.subscription_history || []).length > 0 && (
                <div className="mt-6">
                  <div className="label-eyebrow">Subscription changes</div>
                  <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
                    {billHistory.subscription_history.slice().reverse().map((h, i) => (
                      <div key={`${h.at}-${i}`} className="bg-[#FDFCF8] p-3 flex items-center justify-between gap-3 text-sm">
                        <div>
                          <div className="font-semibold capitalize">{h.tier} · {h.cycle}</div>
                          <div className="text-xs text-[#5C5C5C]">{new Date(h.at).toLocaleString()} · {h.status}</div>
                        </div>
                        <div className="font-display font-bold">{h.status === "CANCELLED" ? "—" : `£${h.final_price ?? h.base_price ?? 0}`}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "team" && (
        <div className="mt-6 max-w-2xl" data-testid="team-section">
          <div className="label-eyebrow flex items-center gap-1.5"><UsersIcon className="w-3 h-3" /> Team members</div>
          <h2 className="font-display font-black text-2xl mt-2">Your hiring team.</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">Add teammates so they share your pipeline and applicant feed. Limits depend on your plan.</p>
          {!team ? (
            <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>
          ) : !team.company ? (
            <div className="mt-6 text-sm text-[#5C5C5C] border border-[#E2E0D8] p-5">
              You don't have a company yet. Create one by signing up as a recruiter, or ask the owner to assign you.
            </div>
          ) : (
            <>
              <div className="mt-6 border border-[#0F0F0F] p-4">
                <div className="label-eyebrow flex items-center gap-1.5">
                  {team.company.name}
                  {Object.keys(team.account_overrides || {}).length > 0 && (
                    <span data-testid="team-custom-quota-badge" className="bg-[var(--brand-primary)] text-white text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5">
                      Custom seats
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5C5C5C] mt-1 font-mono">{team.company.company_id}</div>
                <div className="text-sm mt-2">
                  <strong>{team.members?.length || 0}</strong>
                  {team.plan?.team_members === -1 ? " members · unlimited on your plan" : ` of ${team.plan?.team_members ?? 1} seats used`}
                </div>
              </div>

              {team.is_company_owner && (
                <form onSubmit={addTeamMember} className="mt-5 border border-[#E2E0D8] p-4">
                  <label className="label-eyebrow block mb-1.5">Invite an existing user by email</label>
                  <div className="flex gap-2">
                    <input
                      data-testid="team-add-email"
                      type="email" required
                      value={teamAddEmail}
                      onChange={(e) => setTeamAddEmail(e.target.value)}
                      placeholder="teammate@company.com"
                      className="flex-1 border border-[#0F0F0F] bg-transparent px-3 py-2"
                    />
                    <button data-testid="team-add-btn" type="submit" disabled={!teamAddEmail.trim()} className="btn-terra"><Plus className="w-4 h-4" /> Add</button>
                  </div>
                  <p className="text-xs text-[#5C5C5C] mt-2">They must already have an account. Candidates get promoted to recruiter; existing recruiters must not already be in another company.</p>
                  {teamError && <div data-testid="team-error" className="text-sm text-[var(--brand-primary)] mt-2">{teamError}</div>}
                </form>
              )}

              {team.is_company_owner && (
                <div className="mt-5 border border-[#E2E0D8] p-4 text-left">
                  <label className="label-eyebrow block mb-1.5">Generate Employee Invite Link</label>
                  <p className="text-xs text-[#5C5C5C] mb-3">Create an invite link that employees can click to register and instantly join your company hiring team.</p>
                  <button
                    type="button"
                    onClick={async () => {
                      setGeneratingInvite(true);
                      try {
                        const { data } = await api.post("/recruiter/company/invite");
                        alert(`Invite link generated successfully:\n${data.invite_link}`);
                        loadCompanyInvites();
                      } catch (e) {
                        alert(e.response?.data?.detail || e.message);
                      } finally {
                        setGeneratingInvite(false);
                      }
                    }}
                    data-testid="generate-emp-invite-btn"
                    disabled={generatingInvite}
                    className="text-xs font-bold uppercase tracking-wider py-2 px-4 bg-[var(--brand-primary)] text-white hover:bg-[#B83A28] disabled:opacity-40 transition"
                  >
                    {generatingInvite ? "Generating..." : "Generate Invite Link"}
                  </button>
                  
                  {companyInvites.length > 0 && (
                    <div className="mt-4 border-t border-[#E2E0D8] pt-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C]">Active Invite Links</div>
                      {companyInvites.map((inv) => (
                        <div key={inv.code} className="flex items-center justify-between gap-2 border-b border-[#F3F2EB] py-1.5 flex-wrap">
                          <span className="font-mono text-xs text-[#5C5C5C]">{inv.code} ({inv.uses} uses)</span>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(inv.invite_link);
                              alert("Invite link copied to clipboard!");
                            }}
                            data-testid={`copy-invite-${inv.code}`}
                            className="text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-white px-2 py-1 hover:bg-[#5C5C5C] transition"
                          >
                            Copy Link
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {team.is_company_owner && user.role !== "owner" && (
                <form onSubmit={transferCompany} className="mt-5 border border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5 p-4" data-testid="transfer-founder-card">
                  <div className="label-eyebrow text-[var(--brand-primary)] flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Transfer ownership</div>
                  <h3 className="font-display font-bold text-lg mt-1">Hand this company over to someone else.</h3>
                  <p className="text-sm text-[#5C5C5C] mt-1">
                    Transfers the founder role to another user. They'll inherit billing, branding, and team controls. You'll stay as a regular member.
                    Candidates are auto-promoted to recruiter on transfer.
                  </p>
                  <div className="mt-3 flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <label className="label-eyebrow block mb-1.5">New founder email</label>
                      <input
                        data-testid="transfer-founder-email"
                        type="email" required
                        value={transferEmail}
                        onChange={(e) => setTransferEmail(e.target.value)}
                        placeholder="new.founder@company.com"
                        className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
                      />
                    </div>
                    <button
                      data-testid="transfer-founder-submit"
                      type="submit"
                      disabled={!transferEmail.trim() || transferSubmitting}
                      className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[#FDFCF8] px-4 py-2 font-display font-bold text-sm uppercase tracking-widest hover:bg-[#0F0F0F] disabled:opacity-40"
                    >
                      <UsersIcon className="w-4 h-4" /> {transferSubmitting ? "Transferring…" : "Transfer ownership"}
                    </button>
                  </div>
                </form>
              )}

              {team.is_company_owner && (
                <div className="mt-5 border border-[#0F0F0F] p-4" data-testid="anonymous-review-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="label-eyebrow flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-[var(--brand-primary)]" /> Fair Pipeline — Anonymous review</div>
                      <h3 className="font-display font-bold text-lg mt-1">Hide candidate identity until interview.</h3>
                      <p className="text-sm text-[#5C5C5C] mt-1">
                        When on, recruiters in this company see <em>"Candidate #XYZ"</em> instead of name/email/resume for applications still in pre-interview stages
                        (<strong>Submitted, Under Review, Reviewed, Denied, Flagged</strong>). Identity reveals at <strong>Interview</strong> and beyond.
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        const next = !team.company.anonymous_review;
                        try {
                          await api.put(`/companies/${team.company.company_id}/anonymous-review`, { enabled: next });
                          loadTeam();
                        } catch (e) {
                          setTeamError(formatApiError(e.response?.data?.detail) || e.message);
                        }
                      }}
                      data-testid="anonymous-review-toggle"
                      className={`text-xs px-3 py-1.5 border border-[#0F0F0F] ${team.company.anonymous_review ? "bg-[#0F0F0F] text-[#FDFCF8]" : "hover:bg-[#0F0F0F] hover:text-[#FDFCF8]"}`}
                    >
                      {team.company.anonymous_review ? "ON · click to disable" : "OFF · click to enable"}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-5">
                <div className="label-eyebrow">Members</div>
                <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
                  {(team.members || []).length === 0 && <div className="bg-[#FDFCF8] p-4 text-sm text-[#5C5C5C]">No members yet.</div>}
                  {(team.members || []).map((m) => {
                    const isFounder = team.company.owner_user_id === m.user_id;
                    return (
                      <div key={m.user_id} data-testid={`team-member-${m.user_id}`} className="bg-[#FDFCF8] p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{m.name || m.email}</div>
                          <div className="text-xs text-[#5C5C5C] truncate">{m.email}{m.job_title ? ` · ${m.job_title}` : ""}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isFounder && <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-white px-1.5 py-0.5">Founder</span>}
                          {team.is_company_owner && !isFounder && m.user_id !== user.user_id && (
                            <button onClick={() => removeTeamMember(m.user_id)} data-testid={`team-remove-${m.user_id}`} className="p-1.5 hover:text-[var(--brand-primary)]" title="Remove from team">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "connections" && (
        <div className="mt-6 max-w-2xl" data-testid="connections-section">
          <div className="label-eyebrow flex items-center gap-1.5"><KeyRound className="w-3 h-3 text-[var(--brand-primary)]" /> Connected Accounts</div>
          <h2 className="font-display font-black text-2xl mt-2">Currently Under Development</h2>
          <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed">
            We are looking into the possibility of adding SEO connections. However, at this time nothing is guaranteed. Thank you for your patience.
          </p>
        </div>
      )}

      {tab === "profile" && (
        <form onSubmit={saveProfile} className="mt-6 space-y-4 max-w-lg">
          <div>
            <label className="label-eyebrow block mb-1.5">Full name</label>
            <input data-testid="set-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Phone</label>
            <input data-testid="set-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">LinkedIn URL</label>
            <input data-testid="set-linkedin" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} className={inputCls} placeholder="https://linkedin.com/in/…" />
          </div>
          <button type="submit" disabled={loading} data-testid="save-profile" className="btn-terra">
            <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save profile"}
          </button>
        </form>
      )}

      {tab === "business" && !isCandidate && (
        <>
        <form onSubmit={saveProfile} className="mt-6 space-y-4 max-w-lg">
          <div>
            <label className="label-eyebrow block mb-1.5">Company name *</label>
            <input data-testid="set-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Your job title</label>
            <input data-testid="set-jobtitle" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Company website</label>
            <input data-testid="set-website" type="url" value={form.company_website} onChange={(e) => setForm({ ...form, company_website: e.target.value })} className={inputCls} placeholder="https://" />
          </div>
          <div>
            <div className="label-eyebrow block mb-1.5">Organisation type</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "business", t: "Real business", h: "Standard hiring; no extra notices on listings." },
                { v: "game", t: "Game group", h: "Listings show a 'Not affiliated with any official organisation' notice." },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  data-testid={`set-orgtype-${opt.v}`}
                  onClick={() => setForm({ ...form, org_type: opt.v })}
                  className={`text-sm border px-3 py-2.5 text-left transition ${form.org_type === opt.v ? "border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8]" : "border-[#E2E0D8] hover:border-[#0F0F0F]"}`}
                >
                  <div className="font-semibold">{opt.t}</div>
                  <div className={`text-[10px] mt-0.5 ${form.org_type === opt.v ? "text-[#FDFCF8]/70" : "text-[#5C5C5C]"}`}>{opt.h}</div>
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="save-business" className="btn-terra">
            <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save business"}
          </button>
        </form>

        {/* Custom Pipeline settings — Growth+ */}
        {user.company_id && (
          <div className="mt-8 border-t border-[#E2E0D8] pt-6 max-w-lg text-left">
            <div className="label-eyebrow flex items-center gap-1.5"><Palette className="w-3 h-3 text-[var(--brand-primary)]" /> Custom Pipeline Stages · Growth+</div>
            <h3 className="font-display font-bold text-xl mt-1">Manage Your Hiring Stages</h3>
            <p className="text-xs text-[#5C5C5C] mt-1">Define customized stages for your recruitment pipeline. Leave as-is to use platform defaults.</p>
            
            {!(user.role === "owner" || user.subscription_tier === "growth" || user.subscription_tier === "enterprise" || user.account_overrides?.feature_flags?.custom_pipeline) ? (
              <div className="mt-4 border border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/5 p-4 text-xs flex items-start gap-2">
                <Lock className="w-4 h-4 text-[var(--brand-primary)] shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-[var(--brand-primary)] uppercase tracking-wider">Feature Locked</div>
                  <div className="text-[#5C5C5C] mt-0.5">Pipeline stage customization is available on the Growth plan and higher. Upgrade to unlock custom stages for your hiring team.</div>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {customStages.map((stage, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-xs font-mono text-[#5C5C5C] w-6">{i + 1}.</span>
                    <input
                      data-testid={`custom-stage-input-${i}`}
                      type="text"
                      value={stage}
                      onChange={(e) => {
                        const next = [...customStages];
                        next[i] = e.target.value;
                        setCustomStages(next);
                      }}
                      className="flex-1 border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = customStages.filter((_, j) => j !== i);
                        setCustomStages(next);
                      }}
                      data-testid={`remove-custom-stage-${i}`}
                      className="text-xs px-2 py-1.5 border border-[#E2E0D8] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition bg-white"
                      title="Remove Stage"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setCustomStages([...customStages, ""])}
                    data-testid="add-custom-stage-btn"
                    className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition bg-white text-[#0F0F0F]"
                  >
                    + Add Stage
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setSavingCustomStages(true);
                      try {
                        await api.put(`/companies/${user.company_id}/pipeline-stages`, { stages: customStages });
                        alert("Pipeline stages successfully saved!");
                        loadCustomPipelineInSettings();
                      } catch (e) {
                        alert(e.response?.data?.detail || e.message);
                      } finally {
                        setSavingCustomStages(false);
                      }
                    }}
                    data-testid="save-custom-stages-btn"
                    disabled={savingCustomStages || customStages.length === 0}
                    className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 bg-[var(--brand-primary)] text-white hover:bg-[#B83A28] disabled:opacity-40 transition"
                  >
                    {savingCustomStages ? "Saving..." : "Save Pipeline Stages"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        </>
      )}

      {tab === "email" && (
        <div className="mt-6 max-w-lg" data-testid="email-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email address</div>
          <div className="font-display font-bold text-lg mt-2">{user.email}</div>
          {user.pending_email && (
            <div className="mt-3 border border-amber-300 bg-amber-50 p-3 text-sm">
              <strong>Pending verification:</strong> {user.pending_email}
              <button onClick={cancelEmailChange} data-testid="cancel-email-change" className="ml-2 underline hover:text-[var(--brand-primary)]">cancel</button>
            </div>
          )}
          {user.auth_provider !== "local" ? (
            <p className="text-sm text-[#5C5C5C] mt-4">Your account uses SSO (Google). Email changes must be done with your identity provider.</p>
          ) : (
            <>
              <form onSubmit={requestEmailChange} className="mt-6 space-y-3 border border-[#0F0F0F] p-5">
                <div className="label-eyebrow">Step 1 — request a new email</div>
                <div>
                  <label className="label-eyebrow block mb-1.5">New email</label>
                  <input data-testid="email-new" required type="email" value={emailChange.new_email} onChange={(e) => setEmailChange({ ...emailChange, new_email: e.target.value })} className={inputCls} placeholder="new@example.com" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Confirm with your password</label>
                  <input data-testid="email-password" required type="password" value={emailChange.password} onChange={(e) => setEmailChange({ ...emailChange, password: e.target.value })} className={inputCls} />
                </div>
                <button type="submit" disabled={loading || !emailChange.new_email || !emailChange.password} data-testid="email-request" className="btn-ink">
                  {loading ? "Sending…" : "Send verification email"}
                </button>
              </form>

              <p className="text-xs text-[#5C5C5C] mt-4 italic">A verification link will be sent to the new address. Clicking it confirms the change — no further action needed. The pending email shown above clears automatically after verification. For compliance: changing your email also updates your login and notifications.</p>
            </>
          )}
        </div>
      )}

      {tab === "security" && (
        <div className="mt-6 max-w-lg">
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-1.5">Current password</label>
              <input data-testid="set-current-password" type="password" required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">New password</label>
              <input data-testid="set-new-password" type="password" required minLength={6} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Confirm new password</label>
              <input data-testid="set-confirm-password" type="password" required minLength={6} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={inputCls} />
            </div>
            <button type="submit" disabled={loading} data-testid="change-password" className="btn-terra">
              <KeyRound className="w-4 h-4" /> {loading ? "Updating…" : "Change password"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E2E0D8]">
            <div className="label-eyebrow">Forgot your current password?</div>
            <p className="text-sm text-[#5C5C5C] mt-1">Reset it by email instead.</p>
            <Link to="/forgot-password" data-testid="settings-forgot-link" className="btn-ghost text-sm mt-3 inline-flex">
              Send password reset email
            </Link>
          </div>

          {user.role !== "owner" && (
            <div className="mt-10 pt-6 border-t-2 border-[var(--brand-primary)]/60" data-testid="delete-account-block">
              <div className="label-eyebrow text-[var(--brand-primary)] flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Danger zone</div>
              <h3 className="font-display font-black text-2xl mt-2">Delete my account.</h3>
              <p className="text-sm text-[#5C5C5C] mt-1">
                Permanently removes your profile, applications{user.role === "recruiter" ? ", jobs you posted, and applications received" : ""}.
                Active Stripe subscriptions are cancelled at period end. This cannot be undone.
              </p>
              <form onSubmit={deleteAccount} className="mt-5 space-y-3 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-4">
                <div>
                  <label className="label-eyebrow block mb-1.5">Type your email address to confirm</label>
                  <input
                    data-testid="delete-email-confirm"
                    type="email" required
                    value={del.email_confirm}
                    onChange={(e) => setDel({ ...del, email_confirm: e.target.value })}
                    placeholder={user.email}
                    className={inputCls}
                  />
                </div>
                {user.auth_provider === "local" && (
                  <div>
                    <label className="label-eyebrow block mb-1.5">Confirm with your password</label>
                    <input
                      data-testid="delete-password"
                      type="password" required
                      value={del.password}
                      onChange={(e) => setDel({ ...del, password: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                )}
                {del.error && <div data-testid="delete-error" className="text-sm text-[var(--brand-primary)]">{del.error}</div>}
                <button
                  type="submit"
                  data-testid="delete-account-submit"
                  disabled={del.submitting || del.email_confirm.toLowerCase() !== (user.email || "").toLowerCase() || (user.auth_provider === "local" && !del.password)}
                  className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[#FDFCF8] px-4 py-2 font-display font-bold text-sm uppercase tracking-widest hover:bg-[#0F0F0F] disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" /> {del.submitting ? "Deleting…" : "Permanently delete my account"}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {tab === "templates" && <EmailTemplatesTab />}

      {tab === "platform-templates" && user.role === "owner" && <PlatformEmailTemplatesTab />}

      {tab === "twofa" && (
        <div className="mt-6 max-w-lg" data-testid="twofa-section">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${user.two_factor_enabled ? "bg-green-600" : "bg-[#5C5C5C]"}`} />
            <div className="font-display font-bold text-lg">
              Two-factor authentication is {user.two_factor_enabled ? "ON" : "OFF"}
            </div>
          </div>
          <p className="text-sm text-[#5C5C5C] mt-2">
            Adds a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy) on top of your password.
          </p>

          {!user.two_factor_enabled && !twoFA.qr && (
            <button onClick={start2FA} disabled={loading} data-testid="twofa-start" className="btn-terra mt-5">
              <ShieldCheck className="w-4 h-4" /> {loading ? "Generating…" : "Enable two-factor"}
            </button>
          )}

          {!user.two_factor_enabled && twoFA.qr && (
            <form onSubmit={verify2FA} className="mt-6 space-y-4 border border-[#0F0F0F] p-5">
              <div className="label-eyebrow">Step 1 — scan with your authenticator</div>
              <img src={twoFA.qr} alt="2FA QR" data-testid="twofa-qr" className="w-48 h-48 bg-white p-2 border border-[#E2E0D8]" />
              <details className="text-xs text-[#5C5C5C]">
                <summary className="cursor-pointer font-semibold">Can&apos;t scan? Show manual key</summary>
                <code data-testid="twofa-secret" className="block mt-2 p-2 bg-[#F3F2EB] font-mono tracking-widest break-all">{twoFA.secret}</code>
              </details>
              <div>
                <label className="label-eyebrow block mb-1.5">Step 2 — enter the 6-digit code</label>
                <input
                  data-testid="twofa-verify-code"
                  required
                  inputMode="numeric"
                  pattern="[0-9 ]*"
                  maxLength={9}
                  value={twoFA.code}
                  onChange={(e) => setTwoFA({ ...twoFA, code: e.target.value })}
                  className={inputCls + " font-mono tracking-[0.4em] text-center text-lg"}
                  placeholder="000 000"
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={loading || twoFA.code.length < 6} data-testid="twofa-confirm" className="btn-terra flex-1">
                  <ShieldCheck className="w-4 h-4" /> {loading ? "Verifying…" : "Confirm & turn on"}
                </button>
                <button type="button" onClick={() => setTwoFA({ qr: "", secret: "", uri: "", code: "" })} className="btn-ghost">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {user.two_factor_enabled && (
            <form onSubmit={disable2FA} className="mt-6 space-y-4 border border-[var(--brand-primary)]/40 p-5">
              <div className="label-eyebrow text-[var(--brand-primary)]">Disable two-factor</div>
              <p className="text-xs text-[#5C5C5C]">Confirm with your password (and a current 2FA code if you still have access).</p>
              <div>
                <label className="label-eyebrow block mb-1.5">Password</label>
                <input data-testid="twofa-disable-password" type="password" required value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Current 2FA code (recommended)</label>
                <input data-testid="twofa-disable-code" inputMode="numeric" maxLength={9} value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className={inputCls + " font-mono tracking-[0.4em] text-center"} placeholder="000 000" />
              </div>
              <button type="submit" disabled={loading || !disablePwd} data-testid="twofa-disable" className="btn-ghost">
                <ShieldOff className="w-4 h-4" /> {loading ? "Disabling…" : "Turn off two-factor"}
              </button>
            </form>
          )}
        </div>
      )}


      {tab === "trackers" && (
        <div className="mt-6 max-w-2xl text-left" data-testid="settings-trackers-section">
          <div className="label-eyebrow flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Custom Tracking</div>
          <h2 className="font-display font-black text-2xl mt-2">Track Your Progress</h2>
          <p className="text-sm text-[#5C5C5C] mt-1">Review live progression, reviews, or customized payments linked to your account.</p>

          {/* HR Tracker Administration Panel — available for Enterprise / company_tracking overrides */}
          {(user.role === "owner" || (user.role === "recruiter" && (user.subscription_tier === "enterprise" || user.account_overrides?.feature_flags?.company_tracking))) && (
            <div className="mt-6 border-2 border-[var(--brand-primary)] p-5 bg-[#FDFCF8]" data-testid="hr-trackers-management">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--brand-primary)]">HR Tracker Console</div>
                  <h3 className="font-display font-bold text-lg">Manage Company Progress Trackers</h3>
                </div>
                {!showCreateTracker && !updatingTracker && (
                  <button
                    onClick={() => setShowCreateTracker(true)}
                    data-testid="hr-create-tracker-btn"
                    className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 bg-[var(--brand-primary)] text-white hover:bg-[#B83A28] transition"
                  >
                    + Create Tracker
                  </button>
                )}
              </div>

              {/* Create Tracker Form */}
              {showCreateTracker && (
                <form onSubmit={handleCreateTracker} className="space-y-4 border border-[#E2E0D8] p-4 bg-[#F3F2EB] mb-6" data-testid="hr-create-tracker-form">
                  <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[#0F0F0F]">Create New Progress Tracker</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Tracker Title *</label>
                      <input
                        required
                        type="text"
                        value={newTracker.title}
                        onChange={(e) => setNewTracker({ ...newTracker, title: e.target.value })}
                        placeholder="e.g. Account Setup, Payment Plan"
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-title"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Target ID (User or Company ID) *</label>
                      <input
                        required
                        type="text"
                        value={newTracker.target_id}
                        onChange={(e) => setNewTracker({ ...newTracker, target_id: e.target.value })}
                        placeholder="e.g. usr_123 or comp_456"
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-target-id"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Target Type</label>
                      <select
                        value={newTracker.target_type}
                        onChange={(e) => setNewTracker({ ...newTracker, target_type: e.target.value })}
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-target-type"
                      >
                        <option value="candidate">Candidate</option>
                        <option value="recruiter">Recruiter</option>
                        <option value="custom">Custom/Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Initial Status</label>
                      <select
                        value={newTracker.status}
                        onChange={(e) => setNewTracker({ ...newTracker, status: e.target.value })}
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-status"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Reviewing">Reviewing</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Status Color</label>
                      <select
                        value={newTracker.status_color}
                        onChange={(e) => setNewTracker({ ...newTracker, status_color: e.target.value })}
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-color"
                      >
                        <option value="#5C5C5C">Default (Gray)</option>
                        <option value="#D94833">Red (Terra)</option>
                        <option value="#10B981">Green</option>
                        <option value="#3B82F6">Blue</option>
                        <option value="#F97316">Orange</option>
                        <option value="#8B5CF6">Purple</option>
                        <option value="#EAB308">Yellow</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Custom Tracking Code (Optional)</label>
                      <input
                        type="text"
                        value={newTracker.tracking_code}
                        onChange={(e) => setNewTracker({ ...newTracker, tracking_code: e.target.value })}
                        placeholder="e.g. TRK-MYCODE-123"
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-new-tracker-code"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCreateTracker(false)}
                      className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 border border-[#0F0F0F] bg-white text-[#0F0F0F] hover:bg-[#F3F2EB]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingTracker}
                      className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 bg-[#0F0F0F] text-white hover:bg-[#5C5C5C] disabled:opacity-40"
                      data-testid="hr-create-tracker-submit"
                    >
                      {submittingTracker ? "Creating..." : "Create Tracker"}
                    </button>
                  </div>
                </form>
              )}

              {/* Add Update Form */}
              {updatingTracker && (
                <form onSubmit={handleAddUpdate} className="space-y-4 border border-[var(--brand-primary)] p-4 bg-[#F3F2EB] mb-6" data-testid="hr-add-update-form">
                  <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
                    Add Progress Update for: <span className="text-[#0F0F0F]">{updatingTracker.title}</span> ({updatingTracker.tracking_code})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">New Status</label>
                      <select
                        value={newUpdate.status}
                        onChange={(e) => setNewUpdate({ ...newUpdate, status: e.target.value })}
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-update-status"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Reviewing">Reviewing</option>
                        <option value="On Hold">On Hold</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Status Color</label>
                      <select
                        value={newUpdate.status_color}
                        onChange={(e) => setNewUpdate({ ...newUpdate, status_color: e.target.value })}
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-update-color"
                      >
                        <option value="#5C5C5C">Gray</option>
                        <option value="#D94833">Red (Terra)</option>
                        <option value="#10B981">Green</option>
                        <option value="#3B82F6">Blue</option>
                        <option value="#F97316">Orange</option>
                        <option value="#8B5CF6">Purple</option>
                        <option value="#EAB308">Yellow</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1">Update Message *</label>
                      <textarea
                        required
                        rows={3}
                        value={newUpdate.message}
                        onChange={(e) => setNewUpdate({ ...newUpdate, message: e.target.value })}
                        placeholder="Detail the progress update for the client/candidate..."
                        className="w-full border border-[#0F0F0F] bg-white px-3 py-1.5 text-sm text-[#0F0F0F]"
                        data-testid="hr-update-message"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setUpdatingTracker(null)}
                      className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 border border-[#0F0F0F] bg-white text-[#0F0F0F] hover:bg-[#F3F2EB]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingUpdate}
                      className="text-xs font-bold uppercase tracking-wider py-1.5 px-3 bg-[var(--brand-primary)] text-white hover:bg-[#B83A28] disabled:opacity-40"
                      data-testid="hr-add-update-submit"
                    >
                      {submittingUpdate ? "Posting..." : "Post Progress Update"}
                    </button>
                  </div>
                </form>
              )}

              {/* Manageable Trackers List */}
              <div className="mt-4 space-y-3">
                <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] border-b border-[#E2E0D8] pb-1">Active Tracker Records</div>
                {userTrackers.length === 0 && (
                  <div className="text-sm text-[#5C5C5C] italic py-2">No active progress trackers found. Use &apos;+ Create Tracker&apos; to initiate one.</div>
                )}
                {userTrackers.map((tr) => (
                  <div key={tr.tracker_id} className="border border-[#E2E0D8] bg-[#FDFCF8] p-4 text-sm space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5" style={{ backgroundColor: tr.status_colors?.[tr.status] || '#0F0F0F' }}>{tr.status}</span>
                          <span className="font-mono text-xs text-[#5C5C5C]">{tr.tracking_code}</span>
                        </div>
                        <div className="font-display font-bold text-lg mt-1">{tr.title}</div>
                        <div className="text-xs text-[#5C5C5C] mt-0.5">Target: <code>{tr.target_id}</code> ({tr.target_type})</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setUpdatingTracker(tr); setNewUpdate({ status: tr.status, message: "" }); }}
                          className="text-xs font-bold uppercase tracking-wider py-1 px-2.5 border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition"
                          data-testid={`hr-update-btn-${tr.tracker_id}`}
                        >
                          Add Update
                        </button>
                        {tr.created_by === user.user_id && (
                          <button
                            onClick={() => handleDeleteTracker(tr.tracker_id)}
                            className="text-xs font-bold uppercase tracking-wider py-1 px-2.5 bg-red-600 text-white hover:bg-red-800 transition"
                            data-testid={`hr-delete-btn-${tr.tracker_id}`}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public tracking lookup */}
          <div className="mt-6 border border-[#E2E0D8] p-4 bg-[#F3F2EB]">
            <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] mb-2">Track Any Code (Public Lookup)</div>
            <form onSubmit={lookupTracker} className="flex gap-2">
              <input
                data-testid="settings-tracker-lookup-input"
                type="text"
                required
                value={trackerCodeLookup}
                onChange={(e) => setTrackerCodeLookup(e.target.value)}
                placeholder="e.g. TRK-ABC-123"
                className="flex-1 border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]"
              />
              <button type="submit" disabled={searchingTracker} data-testid="settings-tracker-lookup-btn" className="btn-terra text-xs py-2 px-4">
                {searchingTracker ? "Searching..." : "Track Code"}
              </button>
            </form>

            {searchedTracker && (
              <div className="mt-4 border-t border-[#E2E0D8] pt-3 text-sm" data-testid="settings-searched-tracker-result">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5" style={{ backgroundColor: searchedTracker.status_colors?.[searchedTracker.status] || '#0F0F0F' }}>{searchedTracker.status}</span>
                  <span className="font-mono text-xs font-bold text-[#0F0F0F]">{searchedTracker.tracking_code}</span>
                </div>
                <div className="font-display font-bold text-lg">{searchedTracker.title}</div>
                <div className="text-xs text-[#5C5C5C] mb-3">Last updated on {searchedTracker.updates && searchedTracker.updates.length > 0 ? new Date(searchedTracker.updates[searchedTracker.updates.length - 1].at).toLocaleString() : ""}</div>
                
                <div className="space-y-3 pl-3 border-l-2 border-[#0F0F0F]">
                  {(searchedTracker.updates || []).map((up, i) => (
                    <div key={up.update_id || i} className="text-sm leading-snug">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 bg-[#F3F2EB] border border-[#E2E0D8]" style={{ backgroundColor: searchedTracker.status_colors?.[up.status] || '#F3F2EB', color: searchedTracker.status_colors?.[up.status] ? '#FFF' : '#000', border: searchedTracker.status_colors?.[up.status] ? 'none' : '1px solid #E2E0D8' }}>{up.status}</span>
                        <span className="text-[10px] text-[#5C5C5C]">{new Date(up.at).toLocaleString()}</span>
                      </div>
                      <p className="mt-1 text-[#3a3a3a]">{up.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Logged in user's trackers (for normal candidate view) */}
          {(!user.role || user.role === "candidate" || (user.role === "recruiter" && !(user.subscription_tier === "enterprise" || user.account_overrides?.feature_flags?.company_tracking))) && (
            <div className="mt-8 space-y-3">
              <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] mb-2 border-b border-[#E2E0D8] pb-1">Trackers Linked to Your Account</div>
              {userTrackers.length === 0 && <div className="border border-[#E2E0D8] bg-[#FDFCF8] p-5 text-sm text-[#5C5C5C] italic">No trackers are currently linked to your account. You can lookup general progress codes above.</div>}
              {userTrackers.map((tr) => (
                <div key={tr.tracker_id} data-testid={`user-tracker-${tr.tracker_id}`} className="border border-[#E2E0D8] bg-[#FDFCF8] p-4 text-sm space-y-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5" style={{ backgroundColor: tr.status_colors?.[tr.status] || '#0F0F0F' }}>{tr.status}</span>
                        <span className="font-mono text-xs text-[#5C5C5C]">{tr.tracking_code}</span>
                      </div>
                      <div className="font-display font-bold text-lg mt-1">{tr.title}</div>
                      <div className="text-xs text-[#5C5C5C]">Last updated on {tr.updates && tr.updates.length > 0 ? new Date(tr.updates[tr.updates.length - 1].at).toLocaleString() : ""}</div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-[#F3F2EB] pt-3 pl-3 border-l-2 border-[#0F0F0F]/30">
                    {(tr.updates || []).map((up, i) => (
                      <div key={up.update_id || i} className="text-xs leading-snug">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[9px] uppercase font-bold tracking-widest px-1 text-black border border-[#E2E0D8]" style={{ backgroundColor: tr.status_colors?.[up.status] || '#F3F2EB', color: tr.status_colors?.[up.status] ? '#FFF' : '#000', border: tr.status_colors?.[up.status] ? 'none' : '1px solid #E2E0D8' }}>{up.status}</span>
                          <span className="text-[10px] text-[#5C5C5C]">{new Date(up.at).toLocaleString()}</span>
                        </div>
                        <p className="mt-1 text-[#3a3a3a] leading-tight">{up.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
